/**
 * InternalAuditPage - ISO 22301 Clause 9.2
 * Internal audit scheduling, execution, and non-conformity tracking
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  WizardContainer,
  WizardStep,
} from "../components/WizardComponents";
import {
  ClipboardCheck,
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  User,
  FileText,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Search,
  Download,
  Target,
  Shield,
  AlertCircle,
  Clock,
  ArrowRight,
  Sparkles,
  Bot,
  Loader2,
  TrendingUp,
  Zap,
  Info,
} from "lucide-react";

const AUDIT_TYPES = [
  { id: "full", name: "Full BCMS Audit", description: "Complete ISO 22301 audit" },
  { id: "surveillance", name: "Surveillance Audit", description: "Periodic check" },
  { id: "process", name: "Process Audit", description: "Specific process review" },
  { id: "follow_up", name: "Follow-up Audit", description: "Verify corrective actions" },
];

const AUDIT_STATUS = [
  { id: "planned", name: "Planned", color: "bg-blue-100 text-blue-700" },
  { id: "in_progress", name: "In Progress", color: "bg-amber-100 text-amber-700" },
  { id: "completed", name: "Completed", color: "bg-emerald-100 text-emerald-700" },
  { id: "cancelled", name: "Cancelled", color: "bg-slate-100 text-slate-700" },
];

const FINDING_TYPES = [
  { id: "major_nc", name: "Major Non-Conformity", color: "bg-red-100 text-red-700", icon: XCircle },
  { id: "minor_nc", name: "Minor Non-Conformity", color: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  { id: "observation", name: "Observation", color: "bg-blue-100 text-blue-700", icon: AlertCircle },
  { id: "opportunity", name: "Opportunity for Improvement", color: "bg-emerald-100 text-emerald-700", icon: Target },
];

const ISO_CLAUSES = [
  { id: "4", name: "4. Context of the Organization" },
  { id: "5", name: "5. Leadership" },
  { id: "6", name: "6. Planning" },
  { id: "7", name: "7. Support" },
  { id: "8", name: "8. Operation" },
  { id: "9", name: "9. Performance Evaluation" },
  { id: "10", name: "10. Improvement" },
];

const WIZARD_STEPS = [
  { id: 1, title: "Audit Details", description: "Basic information", icon: FileText },
  { id: 2, title: "Scope & Criteria", description: "What to audit", icon: Target },
  { id: 3, title: "Audit Team", description: "Assign auditors", icon: User },
  { id: 4, title: "Schedule", description: "Plan activities", icon: Calendar },
  { id: 5, title: "Review", description: "Confirm details", icon: CheckCircle2 },
];

export default function InternalAuditPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [audits, setAudits] = useState([]);
  const [findings, setFindings] = useState([]);
  const [activeTab, setActiveTab] = useState("audits");
  const [showWizard, setShowWizard] = useState(false);
  const [showFindingDialog, setShowFindingDialog] = useState(false);
  const [findingStep, setFindingStep] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);

  const [auditForm, setAuditForm] = useState({
    title: "",
    audit_type: "full",
    description: "",
    scope_areas: [],
    criteria: "ISO 22301:2019",
    lead_auditor: "",
    audit_team: "",
    auditee_contact: "",
    planned_start_date: "",
    planned_end_date: "",
    objectives: "",
    status: "planned",
  });

  const [findingForm, setFindingForm] = useState({
    audit_id: "",
    finding_type: "minor_nc",
    clause: "",
    title: "",
    description: "",
    evidence: "",
    root_cause: "",
    corrective_action: "",
    responsible_person: "",
    target_date: "",
    status: "open",
  });

  // AI Compliance Gap Analyzer states
  const [showAIAnalyzer, setShowAIAnalyzer] = useState(false);
  const [aiAnalyzerLoading, setAIAnalyzerLoading] = useState(false);
  const [complianceAnalysis, setComplianceAnalysis] = useState(null);

  const canEdit = user?.role === "bcm_manager" || user?.role === "admin";

  // AI Scope Suggestions (P38 shared banner). Turns accepted enrichment-driven
  // audit-readiness suggestions into a new audit whose scope_areas are seeded.
  const _scopeStr = (x) =>
    typeof x === "string"
      ? x
      : (x?.scope_area || x?.module_name || x?.name || x?.checklist_item ||
         x?.title || x?.action || (x ? String(Object.values(x)[0] ?? "") : ""));
  const auditScopeKeys = audits.flatMap((a) => a.scope_areas || []);

  const handleAcceptScope = async (items) => {
    const areas = [...new Set((items || []).map(_scopeStr).map((s) => String(s).trim()).filter(Boolean))];
    if (areas.length === 0) return { created: 0 };
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await api.post("/internal-audits/", {
        title: `AI-Suggested Audit Scope — ${today}`,
        audit_type: "full",
        criteria: "ISO 22301:2019",
        scope_areas: areas,
        planned_start_date: today,
        status: "planned",
      });
      setAudits((prev) => [...prev, res.data]);
      return { created: areas.length };
    } catch (e) {
      return { created: 0, error: e.response?.data?.detail || "Failed to add scope" };
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [auditsRes, findingsRes] = await Promise.all([
        api.get("/internal-audits/"),
        api.get("/internal-audits/findings/all")
      ]);
      setAudits(auditsRes.data || []);
      setFindings(findingsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch audit data:", error);
      toast.error("Failed to load audit data");
      setAudits([]);
      setFindings([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // AI Compliance Gap Analyzer
  const handleAIAnalysis = async () => {
    setShowAIAnalyzer(true);
    setAIAnalyzerLoading(true);
    setComplianceAnalysis(null);

    try {
      // Build organization stats from current data
      const completedAudits = audits.filter(a => a.status === "completed").length;
      const openFindings = findings.filter(f => f.status === "open").length;
      const majorNCs = findings.filter(f => f.finding_type === "major_nc").length;
      const minorNCs = findings.filter(f => f.finding_type === "minor_nc").length;

      const organizationStats = {
        total_audits: audits.length,
        completed_audits: completedAudits,
        in_progress_audits: audits.filter(a => a.status === "in_progress").length,
        total_findings: findings.length,
        open_findings: openFindings,
        closed_findings: findings.filter(f => f.status === "closed").length,
        major_non_conformities: majorNCs,
        minor_non_conformities: minorNCs,
        observations: findings.filter(f => f.finding_type === "observation").length
      };

      // Build clause compliance based on findings
      const clauseCompliance = {};
      ISO_CLAUSES.forEach(clause => {
        const clauseFindings = findings.filter(f => f.clause?.startsWith(clause.id));
        const openClauseFindings = clauseFindings.filter(f => f.status === "open");
        clauseCompliance[clause.id] = {
          name: clause.name,
          total_findings: clauseFindings.length,
          open_findings: openClauseFindings.length,
          has_major_nc: clauseFindings.some(f => f.finding_type === "major_nc" && f.status === "open"),
          status: openClauseFindings.length === 0 ? "compliant" : 
                  clauseFindings.some(f => f.finding_type === "major_nc") ? "non_compliant" : "partial"
        };
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-assistant/analyze-compliance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organization_stats: organizationStats,
          clause_compliance: clauseCompliance,
          recent_activities: audits.slice(0, 5).map(a => ({
            type: "audit",
            title: a.title,
            status: a.status,
            date: a.planned_start_date
          }))
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setComplianceAnalysis(data.data);
        toast.success("AI compliance analysis complete!");
      } else {
        throw new Error("Analysis failed");
      }
    } catch (error) {
      console.error("AI analysis failed:", error);
      toast.error("Failed to run AI analysis. Please try again.");
    } finally {
      setAIAnalyzerLoading(false);
    }
  };

  const handleAuditSubmit = async () => {
    if (!auditForm.title || !auditForm.planned_start_date) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/internal-audits/", auditForm);
      setAudits(prev => [...prev, response.data]);
      toast.success("Audit scheduled successfully");
      setShowWizard(false);
      resetAuditForm();
    } catch (error) {
      toast.error("Failed to create audit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFindingSubmit = async (e) => {
    e.preventDefault();
    if (!findingForm.title || !findingForm.finding_type || !findingForm.audit_id) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post("/internal-audits/findings", findingForm);
      setFindings(prev => [...prev, response.data]);
      toast.success("Finding recorded successfully");
      setShowFindingDialog(false);
      resetFindingForm();
      // Refresh audits to update findings count
      fetchData();
    } catch (error) {
      toast.error("Failed to record finding");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAuditForm = () => {
    setAuditForm({
      title: "",
      audit_type: "full",
      description: "",
      scope_areas: [],
      criteria: "ISO 22301:2019",
      lead_auditor: "",
      audit_team: "",
      auditee_contact: "",
      planned_start_date: "",
      planned_end_date: "",
      objectives: "",
      status: "planned",
    });
    setCurrentStep(1);
  };

  const resetFindingForm = () => {
    setFindingForm({
      audit_id: "",
      finding_type: "minor_nc",
      clause: "",
      title: "",
      description: "",
      evidence: "",
      root_cause: "",
      corrective_action: "",
      responsible_person: "",
      target_date: "",
      status: "open",
    });
  };

  const getStatusInfo = (statusId) => AUDIT_STATUS.find(s => s.id === statusId) || AUDIT_STATUS[0];
  const getFindingTypeInfo = (typeId) => FINDING_TYPES.find(t => t.id === typeId) || FINDING_TYPES[1];
  const getAuditTypeInfo = (typeId) => AUDIT_TYPES.find(t => t.id === typeId) || AUDIT_TYPES[0];

  // Stats
  const stats = {
    totalAudits: audits.length,
    plannedAudits: audits.filter(a => a.status === "planned").length,
    completedAudits: audits.filter(a => a.status === "completed").length,
    openFindings: findings.filter(f => f.status === "open" || f.status === "in_progress").length,
    majorNCs: findings.filter(f => f.finding_type === "major_nc" && f.status !== "closed").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="internal-audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 9.2</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Internal Audit</h1>
          <p className="text-slate-600 mt-1">
            Plan, execute, and track internal BCMS audits
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button
              onClick={handleAIAnalysis}
              className="bg-gradient-to-r from-sky-700 to-teal-600 hover:from-indigo-700 hover:to-purple-700"
              data-testid="ai-analyzer-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Gap Analyzer
            </Button>
            <Button
              variant="outline"
              onClick={() => { resetFindingForm(); setShowFindingDialog(true); }}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Record Finding
            </Button>
            <Button
              onClick={() => { resetAuditForm(); setShowWizard(true); }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="schedule-audit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Schedule Audit
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Audits</p>
                <p className="text-2xl font-bold text-slate-900">{stats.totalAudits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Planned</p>
                <p className="text-2xl font-bold text-slate-900">{stats.plannedAudits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-slate-900">{stats.completedAudits}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Open Findings</p>
                <p className="text-2xl font-bold text-slate-900">{stats.openFindings}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Major NCs</p>
                <p className="text-2xl font-bold text-slate-900">{stats.majorNCs}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Scope Suggestions (enrichment-driven) — additive, above the audit list */}
      {canEdit && (
        <AISuggestionsBanner
          module="audit_readiness"
          onAccept={handleAcceptScope}
          existingKeys={auditScopeKeys}
          itemKeyOf={(x) => _scopeStr(x).trim().toLowerCase()}
          className="mb-2"
        />
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="audits">Audit Schedule</TabsTrigger>
          <TabsTrigger value="findings">Findings Register</TabsTrigger>
          <TabsTrigger value="program">Audit Program</TabsTrigger>
        </TabsList>

        {/* Audit Schedule Tab */}
        <TabsContent value="audits">
          <Card>
            <CardHeader>
              <CardTitle>Scheduled Audits</CardTitle>
              <CardDescription>Internal audit schedule and status tracking</CardDescription>
            </CardHeader>
            <CardContent>
              {audits.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No audits scheduled yet</p>
                  {canEdit && (
                    <Button className="mt-4" onClick={() => { resetAuditForm(); setShowWizard(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Schedule First Audit
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Audit</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Lead Auditor</TableHead>
                      <TableHead>Dates</TableHead>
                      <TableHead>Findings</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audits.map((audit) => {
                      const statusInfo = getStatusInfo(audit.status);
                      const typeInfo = getAuditTypeInfo(audit.audit_type);

                      return (
                        <TableRow key={audit.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-slate-900">{audit.title}</p>
                              <p className="text-sm text-slate-500">{audit.criteria}</p>
                            </div>
                          </TableCell>
                          <TableCell>{typeInfo.name}</TableCell>
                          <TableCell>{audit.lead_auditor || "-"}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{audit.planned_start_date}</p>
                              <p className="text-slate-500">to {audit.planned_end_date}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{audit.findings_count || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.name}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button size="sm" variant="ghost" onClick={() => setSelectedAudit(audit)} data-testid={`view-audit-${audit.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {canEdit && (
                                <Button size="sm" variant="ghost">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Findings Register Tab */}
        <TabsContent value="findings">
          <Card>
            <CardHeader>
              <CardTitle>Audit Findings</CardTitle>
              <CardDescription>Non-conformities, observations, and opportunities for improvement</CardDescription>
            </CardHeader>
            <CardContent>
              {findings.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No findings recorded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Finding</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Clause</TableHead>
                      <TableHead>Audit</TableHead>
                      <TableHead>Responsible</TableHead>
                      <TableHead>Target Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {findings.map((finding) => {
                      const typeInfo = getFindingTypeInfo(finding.finding_type);
                      const Icon = typeInfo.icon;
                      const isOverdue = finding.target_date && new Date(finding.target_date) < new Date() && finding.status !== "closed";

                      return (
                        <TableRow key={finding.id}>
                          <TableCell>
                            <div className="flex items-start gap-2">
                              <Icon className={`w-4 h-4 mt-1 ${typeInfo.color.replace('bg-', 'text-').replace('-100', '-600')}`} />
                              <div>
                                <p className="font-medium text-slate-900">{finding.title}</p>
                                <p className="text-sm text-slate-500 truncate max-w-[200px]">{finding.description}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={typeInfo.color}>{typeInfo.name}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{finding.clause}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{finding.audit_title}</TableCell>
                          <TableCell>{finding.responsible_person || "-"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {isOverdue && <Clock className="w-4 h-4 text-red-500" />}
                              <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                                {finding.target_date || "-"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              finding.status === "closed" ? "bg-emerald-100 text-emerald-700" :
                              finding.status === "in_progress" ? "bg-amber-100 text-amber-700" :
                              "bg-slate-100 text-slate-700"
                            }>
                              {finding.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Program Tab */}
        <TabsContent value="program">
          <Card>
            <CardHeader>
              <CardTitle>Annual Audit Program</CardTitle>
              <CardDescription>Planned audit coverage for the year</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ISO_CLAUSES.map((clause) => {
                  const auditsForClause = audits.filter(a => a.scope_areas?.includes(clause.id));
                  const coverage = auditsForClause.length > 0 ? 100 : 0;

                  return (
                    <div key={clause.id} className="flex items-center gap-4">
                      <div className="w-48 font-medium text-slate-900">{clause.name}</div>
                      <div className="flex-1">
                        <Progress value={coverage} className="h-3" />
                      </div>
                      <div className="w-20 text-right">
                        <Badge className={coverage > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}>
                          {coverage > 0 ? "Covered" : "Not Covered"}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Audit Detail Dialog */}
      <Dialog open={!!selectedAudit} onOpenChange={() => setSelectedAudit(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedAudit && (
            <div className="space-y-5" data-testid="audit-detail-dialog">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">{selectedAudit.title}</DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={getStatusInfo(selectedAudit.status)?.color}>{getStatusInfo(selectedAudit.status)?.name}</Badge>
                  <Badge variant="outline">{getAuditTypeInfo(selectedAudit.audit_type)?.name}</Badge>
                  <Badge variant="outline">{selectedAudit.criteria}</Badge>
                </div>
              </DialogHeader>

              {selectedAudit.objectives && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Objectives</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedAudit.objectives}</p>
                </div>
              )}

              {selectedAudit.description && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Description</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedAudit.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Lead Auditor</span>
                  <p className="font-medium">{selectedAudit.lead_auditor || "Not assigned"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Audit Team</span>
                  <p className="font-medium">{selectedAudit.audit_team || "Not specified"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Planned Start</span>
                  <p className="font-medium">{selectedAudit.planned_start_date || "Not set"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Planned End</span>
                  <p className="font-medium">{selectedAudit.planned_end_date || "Not set"}</p>
                </div>
                {selectedAudit.actual_start_date && (
                  <div>
                    <span className="text-slate-500">Actual Start</span>
                    <p className="font-medium">{selectedAudit.actual_start_date}</p>
                  </div>
                )}
                {selectedAudit.actual_end_date && (
                  <div>
                    <span className="text-slate-500">Actual End</span>
                    <p className="font-medium">{selectedAudit.actual_end_date}</p>
                  </div>
                )}
                <div>
                  <span className="text-slate-500">Auditee Contact</span>
                  <p className="font-medium">{selectedAudit.auditee_contact || "Not specified"}</p>
                </div>
                <div>
                  <span className="text-slate-500">Findings</span>
                  <p className="font-medium">{selectedAudit.findings_count || 0}</p>
                </div>
              </div>

              {selectedAudit.scope_areas?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Scope (ISO Clauses)</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedAudit.scope_areas.map(c => {
                      const clause = ISO_CLAUSES.find(cl => cl.id === c);
                      return <Badge key={c} variant="outline">{clause ? clause.name : `Clause ${c}`}</Badge>;
                    })}
                  </div>
                </div>
              )}

              {/* Related Findings */}
              {findings.filter(f => f.audit_id === selectedAudit.id).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" /> Related Findings ({findings.filter(f => f.audit_id === selectedAudit.id).length})
                  </h3>
                  <div className="space-y-2">
                    {findings.filter(f => f.audit_id === selectedAudit.id).map((finding) => {
                      const fType = getFindingTypeInfo(finding.finding_type);
                      const FIcon = fType.icon;
                      return (
                        <div key={finding.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                          <FIcon className={`w-4 h-4 mt-0.5 ${fType.color.replace('bg-', 'text-').replace('-100', '-600')}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{finding.title}</span>
                              <Badge className={`${fType.color} text-xs`}>{fType.name}</Badge>
                            </div>
                            {finding.description && <p className="text-xs text-slate-500 mt-1 truncate">{finding.description}</p>}
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                              {finding.clause && <span>Clause {finding.clause}</span>}
                              {finding.responsible_person && <span>{finding.responsible_person}</span>}
                              <Badge variant="outline" className="text-xs">{finding.status}</Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedAudit.summary && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Audit Summary</h3>
                  <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-lg whitespace-pre-wrap">{selectedAudit.summary}</p>
                </div>
              )}

              {selectedAudit.conclusion && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">Conclusion</h3>
                  <p className="text-sm text-slate-600 bg-emerald-50 p-3 rounded-lg whitespace-pre-wrap">{selectedAudit.conclusion}</p>
                </div>
              )}

              <div className="flex justify-end pt-3 border-t">
                <Button variant="outline" onClick={() => setSelectedAudit(null)}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Audit Wizard */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <WizardContainer
            title="Schedule Internal Audit"
            description="Plan a new internal BCMS audit"
            badge="ISO 22301 Clause 9.2"
            steps={WIZARD_STEPS}
            currentStep={currentStep}
            onStepChange={setCurrentStep}
            onComplete={handleAuditSubmit}
            isLoading={submitting}
            showSaveDraft={false}
          >
            {/* Step 1: Audit Details */}
            <WizardStep step={1} currentStep={currentStep}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Audit Title *</Label>
                  <Input
                    value={auditForm.title}
                    onChange={(e) => setAuditForm({ ...auditForm, title: e.target.value })}
                    placeholder="e.g., Annual BCMS Internal Audit 2025"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Audit Type</Label>
                    <Select value={auditForm.audit_type} onValueChange={(v) => setAuditForm({ ...auditForm, audit_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AUDIT_TYPES.map(type => (
                          <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Audit Criteria</Label>
                    <Input
                      value={auditForm.criteria}
                      onChange={(e) => setAuditForm({ ...auditForm, criteria: e.target.value })}
                      placeholder="ISO 22301:2019"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Audit Objectives</Label>
                  <Textarea
                    value={auditForm.objectives}
                    onChange={(e) => setAuditForm({ ...auditForm, objectives: e.target.value })}
                    placeholder="What are the objectives of this audit?"
                    rows={3}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 2: Scope & Criteria */}
            <WizardStep step={2} currentStep={currentStep}>
              <div className="space-y-4">
                <Label>Select ISO 22301 Clauses to Audit</Label>
                <div className="grid grid-cols-2 gap-3">
                  {ISO_CLAUSES.map(clause => (
                    <div
                      key={clause.id}
                      onClick={() => {
                        const current = auditForm.scope_areas || [];
                        const updated = current.includes(clause.id)
                          ? current.filter(c => c !== clause.id)
                          : [...current, clause.id];
                        setAuditForm({ ...auditForm, scope_areas: updated });
                      }}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        auditForm.scope_areas?.includes(clause.id)
                          ? "border-amber-500 bg-amber-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {auditForm.scope_areas?.includes(clause.id) && (
                          <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        )}
                        <span className="text-sm font-medium">{clause.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </WizardStep>

            {/* Step 3: Audit Team */}
            <WizardStep step={3} currentStep={currentStep}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Lead Auditor *</Label>
                  <Input
                    value={auditForm.lead_auditor}
                    onChange={(e) => setAuditForm({ ...auditForm, lead_auditor: e.target.value })}
                    placeholder="Name of lead auditor"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Audit Team Members</Label>
                  <Input
                    value={auditForm.audit_team}
                    onChange={(e) => setAuditForm({ ...auditForm, audit_team: e.target.value })}
                    placeholder="Comma-separated list of team members"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Auditee Contact</Label>
                  <Input
                    value={auditForm.auditee_contact}
                    onChange={(e) => setAuditForm({ ...auditForm, auditee_contact: e.target.value })}
                    placeholder="Primary contact for the auditee"
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 4: Schedule */}
            <WizardStep step={4} currentStep={currentStep}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={auditForm.planned_start_date}
                      onChange={(e) => setAuditForm({ ...auditForm, planned_start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={auditForm.planned_end_date}
                      onChange={(e) => setAuditForm({ ...auditForm, planned_end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description / Notes</Label>
                  <Textarea
                    value={auditForm.description}
                    onChange={(e) => setAuditForm({ ...auditForm, description: e.target.value })}
                    placeholder="Additional details about the audit"
                    rows={3}
                  />
                </div>
              </div>
            </WizardStep>

            {/* Step 5: Review */}
            <WizardStep step={5} currentStep={currentStep}>
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-800 mb-2">Audit Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Title:</p>
                      <p className="font-medium">{auditForm.title || "Not specified"}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Type:</p>
                      <p className="font-medium">{getAuditTypeInfo(auditForm.audit_type).name}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Lead Auditor:</p>
                      <p className="font-medium">{auditForm.lead_auditor || "Not assigned"}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Dates:</p>
                      <p className="font-medium">{auditForm.planned_start_date} to {auditForm.planned_end_date}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-500">Scope (Clauses):</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {auditForm.scope_areas?.map(c => (
                          <Badge key={c} variant="outline">{c}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="flex items-center gap-1.5 text-[0.8rem] text-slate-500" data-testid="audit-immutable-note">
                  <Info className="w-3.5 h-3.5 shrink-0" />
                  All record changes are tracked in an immutable audit trail.
                </p>
              </div>
            </WizardStep>
          </WizardContainer>
        </DialogContent>
      </Dialog>

      {/* Record Finding Dialog */}
      {/* Record Finding Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showFindingDialog}
        onOpenChange={(open) => { setShowFindingDialog(open); if (!open) setFindingStep(0); }}
        title="Record Audit Finding"
        currentStep={findingStep}
        onStepChange={setFindingStep}
        onSubmit={(e) => { if (e && e.preventDefault) e.preventDefault(); handleFindingSubmit(e || { preventDefault: () => {} }); }}
        onCancel={() => setShowFindingDialog(false)}
        submitLabel={submitting ? "Saving..." : "Record Finding"}
        submitDisabled={submitting || !findingForm.audit_id || !findingForm.title}
        saving={submitting}
        canAdvance={
          findingStep === 0 ? !!findingForm.audit_id && !!findingForm.finding_type :
          findingStep === 1 ? !!findingForm.title :
          true
        }
        steps={[
          { id: "classification", title: "Classification", subtitle: "Which audit does this finding relate to, and what type is it?", isComplete: !!findingForm.audit_id && !!findingForm.finding_type },
          { id: "finding", title: "Finding", subtitle: "Describe the finding, the ISO clause, and supporting evidence.", isComplete: !!findingForm.title && !!findingForm.description },
          { id: "action", title: "Action", subtitle: "Document corrective actions and assign responsibility.", isComplete: !!findingForm.corrective_action },
        ]}
      >
        {/* Step 0: Classification */}
        {findingStep === 0 && (
          <div className="space-y-4" data-testid="step-classification">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Related Audit *</Label>
              <Select value={findingForm.audit_id} onValueChange={(v) => setFindingForm({ ...findingForm, audit_id: v })}>
                <SelectTrigger data-testid="audit-select">
                  <SelectValue placeholder="Select audit" />
                </SelectTrigger>
                <SelectContent>
                  {audits.map(audit => (
                    <SelectItem key={audit.id} value={audit.id}>{audit.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Finding Type *</Label>
              <div className="space-y-2">
                {FINDING_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFindingForm({ ...findingForm, finding_type: type.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      findingForm.finding_type === type.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`finding-type-${type.id}`}
                  >
                    <p className="font-medium text-sm">{type.name}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">ISO Clause</Label>
              <Select value={findingForm.clause} onValueChange={(v) => setFindingForm({ ...findingForm, clause: v })}>
                <SelectTrigger data-testid="clause-select">
                  <SelectValue placeholder="Select clause" />
                </SelectTrigger>
                <SelectContent>
                  {ISO_CLAUSES.map(clause => (
                    <SelectItem key={clause.id} value={clause.id}>{clause.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 1: Finding Details */}
        {findingStep === 1 && (
          <div className="space-y-4" data-testid="step-finding">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Finding Title *</Label>
              <Input
                value={findingForm.title}
                onChange={(e) => setFindingForm({ ...findingForm, title: e.target.value })}
                placeholder="Brief title"
                data-testid="finding-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description</Label>
              <Textarea
                value={findingForm.description}
                onChange={(e) => setFindingForm({ ...findingForm, description: e.target.value })}
                placeholder="Detailed description of the finding"
                rows={3}
                data-testid="finding-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Evidence</Label>
              <Textarea
                value={findingForm.evidence}
                onChange={(e) => setFindingForm({ ...findingForm, evidence: e.target.value })}
                placeholder="Evidence observed during audit"
                rows={2}
                data-testid="evidence-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Corrective Action & Assignment */}
        {findingStep === 2 && (
          <div className="space-y-4" data-testid="step-action">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Corrective Action Required</Label>
              <Textarea
                value={findingForm.corrective_action}
                onChange={(e) => setFindingForm({ ...findingForm, corrective_action: e.target.value })}
                placeholder="What action is required to address this finding?"
                rows={3}
                data-testid="corrective-action-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Responsible Person</Label>
              <Input
                value={findingForm.responsible_person}
                onChange={(e) => setFindingForm({ ...findingForm, responsible_person: e.target.value })}
                placeholder="Who is responsible?"
                data-testid="responsible-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Target Date</Label>
              <Input
                type="date"
                value={findingForm.target_date}
                onChange={(e) => setFindingForm({ ...findingForm, target_date: e.target.value })}
                data-testid="target-date-input"
              />
            </div>
            <p className="flex items-center gap-1.5 text-[0.8rem] text-slate-500" data-testid="finding-immutable-note">
              <Info className="w-3.5 h-3.5 shrink-0" />
              All record changes are tracked in an immutable audit trail.
            </p>
          </div>
        )}
      </StepDialog>

      {/* AI Compliance Gap Analyzer Dialog */}
      <Dialog open={showAIAnalyzer} onOpenChange={setShowAIAnalyzer}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <span>AI Compliance Gap Analyzer</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  ISO 22301 Audit Readiness Assessment
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 overflow-y-auto py-4">
            {aiAnalyzerLoading && (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-sky-700 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Analyzing compliance status...</p>
                <p className="text-sm text-slate-400 mt-1">Reviewing audits, findings, and clauses</p>
              </div>
            )}

            {complianceAnalysis && (
              <div className="space-y-6">
                {/* Overall Score Card */}
                <div className="grid grid-cols-3 gap-4">
                  <Card className={`col-span-1 ${
                    complianceAnalysis.overall_score >= 80 ? "border-emerald-300 bg-emerald-50" :
                    complianceAnalysis.overall_score >= 50 ? "border-amber-300 bg-amber-50" :
                    "border-red-300 bg-red-50"
                  }`}>
                    <CardContent className="pt-6 text-center">
                      <div className={`text-5xl font-bold ${
                        complianceAnalysis.overall_score >= 80 ? "text-emerald-600" :
                        complianceAnalysis.overall_score >= 50 ? "text-amber-600" :
                        "text-red-600"
                      }`}>
                        {complianceAnalysis.overall_score || 0}%
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Overall Compliance</p>
                      <Badge className={`mt-2 ${
                        complianceAnalysis.audit_readiness === "Ready" ? "bg-emerald-100 text-emerald-700" :
                        complianceAnalysis.audit_readiness === "Almost Ready" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {complianceAnalysis.audit_readiness || "Needs Work"}
                      </Badge>
                    </CardContent>
                  </Card>
                  <Card className="col-span-2">
                    <CardContent className="pt-6">
                      <h4 className="font-semibold text-slate-900 mb-2">Executive Summary</h4>
                      <p className="text-sm text-slate-600">
                        {complianceAnalysis.executive_summary || "Analysis in progress..."}
                      </p>
                      {complianceAnalysis.estimated_certification_timeline && (
                        <div className="mt-4 flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-sky-700" />
                          <span className="text-slate-600">
                            Est. certification timeline: <strong>{complianceAnalysis.estimated_certification_timeline}</strong>
                          </span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Wins */}
                {complianceAnalysis.quick_wins?.length > 0 && (
                  <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                        <Zap className="w-4 h-4" />
                        Quick Wins (This Week)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {complianceAnalysis.quick_wins.map((win, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-emerald-700">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{win}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Priority Actions */}
                {complianceAnalysis.priority_actions?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Priority Actions
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Action</TableHead>
                            <TableHead>Clause</TableHead>
                            <TableHead>Impact</TableHead>
                            <TableHead>Effort</TableHead>
                            <TableHead>Timeline</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {complianceAnalysis.priority_actions.map((action, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{action.action}</TableCell>
                              <TableCell>{action.clause}</TableCell>
                              <TableCell>
                                <Badge className={
                                  action.impact === "High" ? "bg-red-100 text-red-700" :
                                  action.impact === "Medium" ? "bg-amber-100 text-amber-700" :
                                  "bg-slate-100 text-slate-700"
                                }>
                                  {action.impact}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{action.effort}</Badge>
                              </TableCell>
                              <TableCell>{action.timeline}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Clause Analysis */}
                {complianceAnalysis.clause_analysis?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Clause-by-Clause Analysis
                    </h4>
                    <div className="grid gap-3">
                      {complianceAnalysis.clause_analysis.map((clause, idx) => (
                        <Card key={idx} className={`border ${
                          clause.status === "Compliant" ? "border-emerald-200" :
                          clause.status === "Partial" ? "border-amber-200" :
                          "border-red-200"
                        }`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold">{clause.clause}</span>
                                  <span className="text-slate-600">{clause.title}</span>
                                </div>
                                {clause.gaps?.length > 0 && (
                                  <ul className="mt-2 space-y-1">
                                    {clause.gaps.map((gap, gIdx) => (
                                      <li key={gIdx} className="text-sm text-slate-500 flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-500" />
                                        {gap}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div className="text-right">
                                <div className={`text-2xl font-bold ${
                                  clause.score >= 80 ? "text-emerald-600" :
                                  clause.score >= 50 ? "text-amber-600" :
                                  "text-red-600"
                                }`}>
                                  {clause.score}%
                                </div>
                                <Badge className={`text-xs ${
                                  clause.status === "Compliant" ? "bg-emerald-100 text-emerald-700" :
                                  clause.status === "Partial" ? "bg-amber-100 text-amber-700" :
                                  "bg-red-100 text-red-700"
                                }`}>
                                  {clause.status}
                                </Badge>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Strengths */}
                {complianceAnalysis.strengths?.length > 0 && (
                  <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
                    <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Strengths
                    </h4>
                    <ul className="space-y-1">
                      {complianceAnalysis.strengths.map((strength, idx) => (
                        <li key={idx} className="text-sm text-sky-700 flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 mt-0.5" />
                          {strength}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowAIAnalyzer(false)}>
              Close
            </Button>
            {complianceAnalysis && (
              <Button
                onClick={() => {
                  toast.success("Report saved! Use this as your audit preparation baseline.");
                  setShowAIAnalyzer(false);
                }}
                className="bg-sky-700 hover:bg-sky-800"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
